<template>
  <div class="LKPublicationsFormdataOcrTable">
    <LKPublicationsFormdataOcrResultsTable
      v-if="showResults"
      :form-data-id="formDataId"
      :publication-id="publicationId"
      :task-id="selectedTaskId"
      :all_cells="all_cells"
      @back="onBackToStatus"
      @view-details="onViewDetails"
    />

    <div v-else>
      <div class="d-flex align-center" style="margin-bottom: 12px;">
        <div class="text-h6 flex-grow-1">
          Статус обработки файлов
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="list"
        :loading="isLoading"
        disable-pagination
        hide-default-footer
        class="elevation-1"
      >
        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template #item.task_status="{ item }">
          <div style="display: inline-flex; align-items: center;">
            <v-icon
              small
              :color="getStatusColor(item.task_status)"
              class="mr-1"
            >
              {{ getStatusIcon(item.task_status) }}
            </v-icon>
            <span :class="`${getStatusColor(item.task_status)}--text font-weight-medium`">
              {{ getStatusText(item.task_status) }}
            </span>
          </div>
        </template>

        <template #item.results="{ item }">
          <div v-if="item.task_status === 'DONE'" class="d-flex align-center">
            <div v-if="item.ready_count > 0" class="d-flex align-center mr-3">
              <v-icon small color="success" class="mr-1">
                mdi-check-circle
              </v-icon>
              <span class="success--text font-weight-medium">
                {{ item.ready_count }}
              </span>
            </div>
            <div v-if="item.errors_count > 0" class="d-flex align-center">
              <v-icon small color="error" class="mr-1">
                mdi-alert-circle
              </v-icon>
              <span class="error--text font-weight-medium">
                {{ item.errors_count }}
              </span>
            </div>
          </div>
          <span v-else>—</span>
        </template>

        <template #item.open="{ item }">
          <v-btn
            v-if="item.task_status === 'DONE'"
            color="primary"
            small
            outlined
            :disabled="item.ready_count === 0 && item.errors_count === 0"
            @click="onOpenResults(item)"
          >
            Открыть результаты
          </v-btn>
        </template>

        <template #item.delete="{ item }">
          <v-btn
            icon
            color="error"
            small
            @click="onDeleteClick(item)"
          >
            <v-icon small>
              mdi-delete
            </v-icon>
          </v-btn>
        </template>

        <template #no-data>
          <div class="text-center pa-4">
            Нет загруженных файлов
          </div>
        </template>
      </v-data-table>
    </div>

    <v-dialog
      v-model="deleteDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title class="text-h6">
          Подтверждение удаления
        </v-card-title>
        <v-card-text>
          Вы уверены, что хотите удалить результаты обработки файла
          <strong>{{ itemToDelete && itemToDelete.file_filename }}</strong>?
          <br>
          Это действие невозможно отменить.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="isDeleting"
            text
            outlined
            @click="onCancelDelete"
          >
            Отмена
          </v-btn>
          <v-btn
            :loading="isDeleting"
            color="error"
            outlined
            @click="onConfirmDelete"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { CollectorOcrApi } from '@/api/resources/Collector/CollectorOcrApi'
import { dateFormatter } from '@/helpers/dateFormatter'
import LKPublicationsFormdataOcrResultsTable from './LKPublicationsFormdataOcrResultsTable.vue'

export default {
  name: 'LKPublicationsFormdataOcrTable',
  components: {
    LKPublicationsFormdataOcrResultsTable,
  },
  props: {
    formDataId: {
      type: [String, Number],
      required: true,
    },
    publicationId: {
      type: [String, Number],
      required: true,
    },
    all_cells: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    isLoading: false,
    list: [],
    showResults: false,
    selectedTaskId: null,
    deleteDialog: false,
    itemToDelete: null,
    isDeleting: false,
    pollingTimer: null,
    headers: [
      {
        text: 'Имя файла',
        value: 'file_filename',
        sortable: false,
      },
      {
        text: 'Дата загрузки',
        value: 'created_at',
        sortable: false,
      },
      {
        text: 'Статус',
        value: 'task_status',
        sortable: false,
      },
      {
        text: 'Результаты',
        value: 'results',
        sortable: false,
      },
      {
        text: '',
        value: 'open',
        sortable: false,
        align: 'right',
        width: '180px',
      },
      {
        text: '',
        value: 'delete',
        sortable: false,
        align: 'center',
        width: '60px',
      },
    ],
  }),
  mounted() {
    this.loadData()
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    startPolling() {
      this.pollingTimer = setInterval(() => {
        if (!this.isLoading && !this.showResults) {
          this.loadData()
        }
      }, 5000)
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    async loadData() {
      if (!this.formDataId || !this.publicationId) {
        return
      }

      this.isLoading = true
      const api = new CollectorOcrApi()
      try {
        const response = await api.getStatus(this.formDataId, this.publicationId)
        this.list = response.data || []
        this.emitScanningStatus()
      } catch (error) {
        this.$notice.errors(error)
      } finally {
        this.isLoading = false
      }
    },
    emitScanningStatus() {
      if (this.list.length === 0) {
        this.$emit('status-change', null)
        return
      }
      const hasPending = this.list.some((t) => ['PENDING', 'IN_PROGRESS'].includes(t.task_status))
      this.$emit('status-change', hasPending ? 'processing' : 'done')
    },
    resetView() {
      // Сбрасываем состояние просмотра результатов
      this.showResults = false
      this.selectedTaskId = null
    },
    onRefresh() {
      this.loadData()
    },
    formatDate(dateString) {
      return dateFormatter(dateString, 'DD.MM.YYYY HH:mm')
    },
    getStatusText(status) {
      const statusMap = {
        DONE: 'Завершено',
        PENDING: 'В очереди',
        IN_PROGRESS: 'Обрабатывается',
        ERROR: 'Ошибка',
      }
      return statusMap[status] || status
    },
    getStatusColor(status) {
      const colorMap = {
        DONE: 'success',
        PENDING: 'info',
        IN_PROGRESS: 'warning',
        ERROR: 'error',
      }
      return colorMap[status] || 'grey'
    },
    getStatusIcon(status) {
      const iconMap = {
        DONE: 'mdi-check-circle',
        PENDING: 'mdi-clock-outline',
        IN_PROGRESS: 'mdi-sync',
        ERROR: 'mdi-alert-circle',
      }
      return iconMap[status] || 'mdi-help-circle'
    },
    onOpenResults(item) {
      this.selectedTaskId = item.task_id
      this.showResults = true
    },
    onBackToStatus() {
      this.resetView()
      this.loadData()
    },
    onViewDetails(item) {
      // Здесь будет открытие модального окна с деталями
      this.$emit('view-details', item)
    },
    onDeleteClick(item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },
    async onConfirmDelete() {
      if (!this.itemToDelete) {
        return
      }

      this.isDeleting = true
      const api = new CollectorOcrApi()
      try {
        await api.deleteFsdOcrData(this.formDataId, this.itemToDelete.task_id)
        this.$notice.create('Данные успешно удалены')
        await this.loadData()
      } catch (error) {
        this.$notice.errors(error)
      } finally {
        this.isDeleting = false
        this.deleteDialog = false
        this.itemToDelete = null
      }
    },
    onCancelDelete() {
      this.deleteDialog = false
      this.itemToDelete = null
    },
  },
}
</script>

<style lang="scss" scoped>
.LKPublicationsFormdataOcrTable {
  margin-top: 2rem;
  padding: 1rem 2rem;
}
</style>
